/**
 * **What a finished fight still accepts.** After the GM's `end_combat` (`combatEndedAt` set) the
 * reducer refuses every command this table marks `true`: the turn, activation and
 * action-economy family, which only means something while the fight runs. Everything marked
 * `false` stays open for the wrap-up: GM edits (HP, heat, structure, statuses, rulings), token
 * moves and placement (with their hook chains, whose `apply_resolved` recomputes must land),
 * terrain, doors, rooms, annotations, disclosure and every internal inverse (`set_activation`,
 * `restore_*`), so a player's undo below the end still replays.
 *
 * Resolution intents (attacks, techs, Move, Overwatch) never reach the reducer as such; the room
 * refuses all of them with {@link COMBAT_OVER_REASON} (`backend/src/encounterRoomCore.ts`).
 *
 * Total over the leaf union (`satisfies Record<…, boolean>`), so a new command type breaks the
 * build until someone classifies it.
 */

import { CommandError, err, ok, type Result } from "@lancer-vtt/shared";
import type { EncounterLeafCommand } from "./commands";
import { isCombatOver, type EncounterState } from "./types";

export const COMBAT_PLAY_COMMANDS = {
  add_combatant: false,
  remove_combatant: false,
  set_hp: false,
  set_heat: false,
  set_structure: false,
  set_stress: false,
  set_burn: false,
  set_affordance_state: false,
  set_weapon_loaded: false,
  apply_status: false,
  remove_status: false,
  grant_ruling: false,
  grant_move: true,
  restore_statuses: false,
  set_combatant_name: false,
  set_concealed: false,
  set_disclosure: false,
  set_token_image: false,
  set_hp_disclosure: false,
  rename_encounter: false,
  end_combat: false, // rejected on its own when the fight is already over
  reopen_combat: false,
  set_grid_mode: false,
  resize_grid: false,
  set_environment: false,
  set_ground: false,
  set_sitrep: false,
  edit_combatant_base: false,
  set_controller: false,
  set_door_state: false,
  restore_door_state: false,
  move_token: false,
  walk_token: false,
  place_token: false,
  teleport_token: false,
  stand_up: false,
  restore_stand: false,
  set_terrain: false,
  set_edge: false,
  apply_terrain_map: false,
  set_room: false,
  remove_room: false,
  add_annotation: false,
  remove_annotation: false,
  set_annotation: false,
  order_combatants: false,
  end_round: true,
  activate_unit: true,
  end_turn: true,
  stamp_activated: true,
  unstamp_activated: false,
  restore_combatant: false,
  restore_combatant_base: false,
  restore_pos: false,
  set_activation: false, // the GM's override and every turn command's inverse
  spend_action: true,
  spend_reaction: true,
  record_move_reaction_decline: true,
  set_split_move: true,
  restore_budget: false,
  boost: true,
  grant_overcharge: true,
  grant_action_allowance: true,
  open_attack: true,
  close_attack: true,
  convert_barrage_to_skirmish: true,
  prepare: true,
  drop_prepare: true,
  spend_action_use: true,
  apply_resolved: false, // move/door hook chains emit it; intents are refused at the room instead
} satisfies Record<EncounterLeafCommand["type"], boolean>;

/** The refusal every turn command and resolution intent gets once the fight is over. */
export const COMBAT_OVER_REASON = "combat is over (END COMBAT) — undo the end to resume the fight";

/** `err` when `type` is a {@link COMBAT_PLAY_COMMANDS} member and the fight is over. */
export function checkCombatLive(state: EncounterState, type: EncounterLeafCommand["type"]): Result<void, CommandError> {
  if (COMBAT_PLAY_COMMANDS[type] && isCombatOver(state)) return err(new CommandError(`${type}: ${COMBAT_OVER_REASON}`));
  return ok(undefined);
}
